import { Trash2 } from "lucide-react";
import { useState } from "react";
import { formatRateMonth } from "@/components/admin/internal-rate-format";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useCan } from "@/hooks/use-can";
import {
  useRemoveResourceInternalRate,
  useResourceInternalRateHistory,
} from "@/hooks/use-resource-internal-rates";
import { formatPlanMoney } from "@/lib/plan-format";
import { cn } from "@/lib/utils";
import type { ResourceInternalRateRowView } from "@/schemas/resource-internal-rates";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: ResourceInternalRateRowView;
  stepUpId: string;
}

/**
 * Every Internal Rate a Resource has had, newest first. Rows are never edited: a wrong
 * rate is removed (with a reason, audited) and a new one added.
 */
export function InternalRateHistoryDialog({ open, onOpenChange, row, stepUpId }: Props) {
  const canManage = useCan("manageResourceInternalRates");
  const history = useResourceInternalRateHistory(stepUpId, row.resourceId, open);
  const remove = useRemoveResourceInternalRate(stepUpId);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Internal Rate history — {row.resourceName}</DialogTitle>
          <DialogDescription>
            Each rate runs from its month until the next one starts. Removed rates stay here as a
            record but no longer price anything.
          </DialogDescription>
        </DialogHeader>

        {history.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : history.isError ? (
          <ErrorState
            message={history.error.message}
            onRetry={() => {
              history.refetch();
            }}
          />
        ) : history.data === undefined || history.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No rates. This Resource is costed at their Labor Category on every plan.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
            {history.data.map((rate) => (
              <li key={rate.id} className="flex flex-col gap-2 px-3 py-2 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <div
                    className={cn(
                      "flex flex-col gap-0.5",
                      rate.removedAt !== null && "text-muted-foreground",
                    )}
                  >
                    <span
                      className={cn(
                        "font-medium tabular-nums",
                        rate.removedAt !== null && "line-through",
                      )}
                    >
                      {formatPlanMoney(rate.internalCost)}/hr from{" "}
                      {formatRateMonth(rate.effectiveMonth)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Added {new Date(rate.createdAt).toLocaleDateString()}
                      {rate.createdByName !== null && ` by ${rate.createdByName}`}
                      {rate.note !== null && ` · ${rate.note}`}
                    </span>
                    {rate.removedAt !== null && (
                      <span className="text-xs">
                        Removed {new Date(rate.removedAt).toLocaleDateString()}
                        {rate.removedByName !== null && ` by ${rate.removedByName}`}
                        {rate.removalReason !== null && ` · ${rate.removalReason}`}
                      </span>
                    )}
                  </div>
                  {canManage && rate.removedAt === null && removingId !== rate.id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setRemovingId(rate.id);
                        setReason("");
                      }}
                    >
                      <Trash2 className="size-4" aria-hidden />
                      Remove
                    </Button>
                  )}
                </div>
                {removingId === rate.id && (
                  <form
                    className="flex items-center gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      remove.mutate(
                        { rateId: rate.id, reason },
                        { onSuccess: () => setRemovingId(null) },
                      );
                    }}
                  >
                    <Input
                      autoFocus
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Why? Don't include the amount — RMs can read this reason."
                      aria-label="Reason for removing this rate"
                    />
                    <Button
                      type="submit"
                      variant="destructive"
                      disabled={reason.trim().length === 0 || remove.isPending}
                    >
                      Remove
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setRemovingId(null)}>
                      Cancel
                    </Button>
                  </form>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
